import axios from 'utils/axios';
import type {
    CatalogDirActionResultType,
    GetTaskListParamsType,
    GetTaskListResultType,
    GetProcessRunListParamsType,
    GetProcessRunListResultType,
    GetProcessTemplateListParamsType,
    GetProcessTemplateListResultType,
    GetTaskTemplateListParamsType,
    GetTaskTemplateListResultType,
    ProcessDiagramPositionsSaveDataType,
    ProcessRunSaveDataType,
    ProcessRunType,
    ProcessTemplateSaveDataType,
    ProcessTemplateType,
    TaskCardType,
    TaskCreateDataType,
    TaskExecutorTermDataType,
    TaskRejectAnswerDataType,
    TaskSubmitAnswerDataType,
    TaskTemplateCardType,
    TaskTemplateSaveDataType,
    RecurrentInstancesResult,
    TaskUpdateBindingDataType,
    TaskUpdateCategoryDataType,
    TaskUpdatePaymentDataType,
    TaskUpdateDatesDataType,
    TaskUpdateKanbanHideDataType,
    TaskUpdateNoEarlyStartDataType,
    TaskUpdateUsersDataType,
} from '@econtractor/types';

export interface CreateTaskResult {
    stat: boolean;
    taskId?: number;
    errCode?: string;
}

export interface TaskActionResult {
    stat: boolean;
    task?: TaskCardType | null;
    errCode?: string;
    closed?: boolean;
}

/** Axios interceptor відхиляє проміс тілом відповіді, а не AxiosError. */
const taskApiError = <T extends { stat: boolean; errCode?: string }>(e: unknown): T | null => {
    if (e && typeof e === 'object' && ('stat' in e || 'errCode' in e)) {
        return e as T;
    }
    if (e && typeof e === 'object' && 'response' in e) {
        const data = (e as { response?: { data?: T } }).response?.data;
        if (data && typeof data === 'object' && ('stat' in data || 'errCode' in data)) return data;
    }
    console.error(e);
    return null;
};

export const getList = async (
    params: GetTaskListParamsType,
): Promise<GetTaskListResultType | null> => {
    try {
        const response = await axios('/tasks', { params });
        return response?.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const getTask = async (taskId: number): Promise<TaskCardType | null> => {
    try {
        const response = await axios(`/tasks/${taskId}`);
        return response?.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const createTask = async (data: TaskCreateDataType): Promise<CreateTaskResult | null> => {
    try {
        const response = await axios.post('/tasks/new', data);
        return response?.data || null;
    } catch (e) {
        return taskApiError(e);
    }
};

export const updateTaskStatus = async (
    taskId: number,
    status: number,
): Promise<TaskActionResult | null> => {
    try {
        const response = await axios.patch(`/tasks/${taskId}/status`, { status });
        return response?.data || null;
    } catch (e) {
        return taskApiError(e);
    }
};

export const updateTaskDates = async (
    taskId: number,
    data: TaskUpdateDatesDataType,
): Promise<TaskActionResult | null> => {
    try {
        const response = await axios.patch(`/tasks/${taskId}/dates`, data);
        return response?.data || null;
    } catch (e) {
        return taskApiError(e);
    }
};

export const updateExecutorTerm = async (
    taskId: number,
    data: TaskExecutorTermDataType,
): Promise<TaskActionResult | null> => {
    try {
        const response = await axios.patch(`/tasks/${taskId}/executor-term`, data);
        return response?.data || null;
    } catch (e) {
        return taskApiError(e);
    }
};

export const updateTaskUsers = async (
    taskId: number,
    data: TaskUpdateUsersDataType,
): Promise<TaskActionResult | null> => {
    try {
        const response = await axios.patch(`/tasks/${taskId}/users`, data);
        return response?.data || null;
    } catch (e) {
        return taskApiError(e);
    }
};

export const updateTaskCategory = async (
    taskId: number,
    data: TaskUpdateCategoryDataType,
): Promise<TaskActionResult | null> => {
    try {
        const response = await axios.patch(`/tasks/${taskId}/category`, data);
        return response?.data || null;
    } catch (e) {
        return taskApiError(e);
    }
};

export const updateTaskBinding = async (
    taskId: number,
    data: TaskUpdateBindingDataType,
): Promise<TaskActionResult | null> => {
    try {
        const response = await axios.patch(`/tasks/${taskId}/binding`, data);
        return response?.data || null;
    } catch (e) {
        return taskApiError(e);
    }
};

export const updateTaskPayment = async (
    taskId: number,
    data: TaskUpdatePaymentDataType,
): Promise<TaskActionResult | null> => {
    try {
        const response = await axios.post(`/tasks/${taskId}/payment`, data);
        return response?.data || null;
    } catch (e) {
        return taskApiError(e);
    }
};

export const updateTaskNoEarlyStart = async (
    taskId: number,
    data: TaskUpdateNoEarlyStartDataType,
): Promise<TaskActionResult | null> => {
    try {
        const response = await axios.patch(`/tasks/${taskId}/no-early-start`, data);
        return response?.data || null;
    } catch (e) {
        return taskApiError(e);
    }
};

export const updateTaskKanbanHide = async (
    taskId: number,
    data: TaskUpdateKanbanHideDataType,
): Promise<TaskActionResult | null> => {
    try {
        const response = await axios.patch(`/tasks/${taskId}/kanban-hide`, data);
        return response?.data || null;
    } catch (e) {
        return taskApiError(e);
    }
};

export const submitTaskAnswer = async (
    taskId: number,
    data: TaskSubmitAnswerDataType,
): Promise<TaskActionResult | null> => {
    try {
        const response = await axios.post(`/tasks/${taskId}/answer`, data);
        return response?.data || null;
    } catch (e) {
        return taskApiError(e);
    }
};

export const rejectTaskAnswer = async (
    taskId: number,
    data: TaskRejectAnswerDataType,
): Promise<TaskActionResult | null> => {
    try {
        const response = await axios.post(`/tasks/${taskId}/reject`, data);
        return response?.data || null;
    } catch (e) {
        return taskApiError(e);
    }
};

export const remindTaskExecutor = async (taskId: number): Promise<TaskActionResult | null> => {
    try {
        const response = await axios.post(`/tasks/${taskId}/remind`);
        return response?.data || null;
    } catch (e) {
        return taskApiError(e);
    }
};

export const deleteTask = async (taskId: number): Promise<TaskActionResult | null> => {
    try {
        const response = await axios.delete(`/tasks/${taskId}`);
        return response?.data || null;
    } catch (e) {
        return taskApiError(e);
    }
};

export interface TaskTemplateMutationResult {
    stat: boolean;
    templateId?: number;
    template?: TaskTemplateCardType;
    errCode?: string;
}

export const getTaskTemplates = async (
    params: GetTaskTemplateListParamsType = {},
): Promise<GetTaskTemplateListResultType | null> => {
    try {
        const response = await axios('/tasks/templates', { params });
        return response?.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const getTaskTemplate = async (templateId: number): Promise<TaskTemplateCardType | null> => {
    try {
        const response = await axios(`/tasks/templates/${templateId}`);
        return response?.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const createTaskTemplate = async (
    data: TaskTemplateSaveDataType,
): Promise<TaskTemplateMutationResult | null> => {
    try {
        const response = await axios.post('/tasks/templates', data);
        return response?.data || null;
    } catch (e) {
        return taskApiError(e);
    }
};

export const updateTaskTemplate = async (
    templateId: number,
    data: TaskTemplateSaveDataType,
): Promise<TaskTemplateMutationResult | null> => {
    try {
        const response = await axios.patch(`/tasks/templates/${templateId}`, data);
        return response?.data || null;
    } catch (e) {
        return taskApiError(e);
    }
};

const handleDirApiError = (e: unknown): CatalogDirActionResultType | null => {
    if (e && typeof e === 'object' && ('stat' in e || 'errCode' in e)) {
        return e as CatalogDirActionResultType;
    }
    if (e && typeof e === 'object' && 'response' in e) {
        const response = (e as { response?: { data?: CatalogDirActionResultType } }).response;
        if (response?.data) return response.data;
    }
    console.error(e);
    return null;
};

export const getTaskCategoryUsage = async (): Promise<Record<number, number> | null> => {
    try {
        const response = await axios.get('/tasks/templates/dirs/category/usage');
        return response.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const createTaskCategory = async (name: string): Promise<CatalogDirActionResultType | null> => {
    try {
        const response = await axios.post('/tasks/templates/dirs/category/create', { name });
        return response.data;
    } catch (e) {
        return handleDirApiError(e);
    }
};

export const renameTaskCategory = async (
    dirId: number,
    name: string,
): Promise<CatalogDirActionResultType | null> => {
    try {
        const response = await axios.post(`/tasks/templates/dirs/category/update/${dirId}`, { name });
        return response.data;
    } catch (e) {
        return handleDirApiError(e);
    }
};

export const deleteTaskCategory = async (dirId: number): Promise<CatalogDirActionResultType | null> => {
    try {
        const response = await axios.post(`/tasks/templates/dirs/category/delete/${dirId}`);
        return response.data;
    } catch (e) {
        return handleDirApiError(e);
    }
};

export const getRecurrentInstances = async (taskId: number): Promise<RecurrentInstancesResult | null> => {
    try {
        const response = await axios(`/tasks/${taskId}/instances`);
        return response?.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const syncRecurrentFiles = async (taskId: number): Promise<{ stat: boolean } | null> => {
    try {
        const response = await axios.post(`/tasks/${taskId}/recurrent-sync-files`);
        return response?.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const stopRecurrentSeries = async (taskId: number, comment: string): Promise<TaskActionResult | null> => {
    try {
        const response = await axios.post(`/tasks/${taskId}/recurrent-stop`, { comment });
        return response?.data || null;
    } catch (e) {
        return taskApiError(e);
    }
};

export const deleteFreeRecurrentTasks = async (taskId: number, comment: string): Promise<TaskActionResult | null> => {
    try {
        const response = await axios.post(`/tasks/${taskId}/recurrent-delete-free`, { comment });
        return response?.data || null;
    } catch (e) {
        return taskApiError(e);
    }
};

export const cloneTaskTemplate = async (
    templateId: number,
    data: TaskTemplateSaveDataType,
): Promise<TaskTemplateMutationResult | null> => {
    try {
        const response = await axios.post(`/tasks/templates/${templateId}/clone`, data);
        return response?.data || null;
    } catch (e) {
        return taskApiError(e);
    }
};

export interface ProcessTemplateMutationResult {
    stat: boolean;
    templateId?: number;
    template?: ProcessTemplateType;
    errCode?: string;
}

export const getProcessTemplates = async (
    params: GetProcessTemplateListParamsType = {},
): Promise<GetProcessTemplateListResultType | null> => {
    try {
        const response = await axios('/tasks/process-templates', { params });
        return response?.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const getProcessTemplate = async (templateId: number): Promise<ProcessTemplateType | null> => {
    try {
        const response = await axios(`/tasks/process-templates/${templateId}`);
        return response?.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const createProcessTemplate = async (
    data: ProcessTemplateSaveDataType,
): Promise<ProcessTemplateMutationResult | null> => {
    try {
        const response = await axios.post('/tasks/process-templates', data);
        return response?.data || null;
    } catch (e) {
        return taskApiError(e);
    }
};

export const updateProcessTemplate = async (
    templateId: number,
    data: ProcessTemplateSaveDataType,
): Promise<ProcessTemplateMutationResult | null> => {
    try {
        const response = await axios.patch(`/tasks/process-templates/${templateId}`, data);
        return response?.data || null;
    } catch (e) {
        return taskApiError(e);
    }
};

export const deleteProcessTemplate = async (templateId: number): Promise<{ stat: boolean } | null> => {
    try {
        const response = await axios.delete(`/tasks/process-templates/${templateId}`);
        return response?.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const getProcessCategoryUsage = async (): Promise<Record<number, number> | null> => {
    try {
        const response = await axios.get('/tasks/process-templates/dirs/category/usage');
        return response.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const createProcessCategory = async (name: string): Promise<CatalogDirActionResultType | null> => {
    try {
        const response = await axios.post('/tasks/process-templates/dirs/category/create', { name });
        return response.data;
    } catch (e) {
        return handleDirApiError(e);
    }
};

export const renameProcessCategory = async (
    dirId: number,
    name: string,
): Promise<CatalogDirActionResultType | null> => {
    try {
        const response = await axios.post(`/tasks/process-templates/dirs/category/update/${dirId}`, { name });
        return response.data;
    } catch (e) {
        return handleDirApiError(e);
    }
};

export const deleteProcessCategory = async (dirId: number): Promise<CatalogDirActionResultType | null> => {
    try {
        const response = await axios.post(`/tasks/process-templates/dirs/category/delete/${dirId}`);
        return response.data;
    } catch (e) {
        return handleDirApiError(e);
    }
};

export interface ProcessRunMutationResult {
    stat: boolean;
    processId?: number;
    process?: ProcessRunType;
    errCode?: string;
}

export const getProcessRuns = async (
    params: GetProcessRunListParamsType = {},
): Promise<GetProcessRunListResultType | null> => {
    try {
        const response = await axios('/tasks/processes', { params });
        return response?.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const getProcessRun = async (processId: number): Promise<ProcessRunType | null> => {
    try {
        const response = await axios(`/tasks/processes/${processId}`);
        return response?.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const createProcessRun = async (templateId: number): Promise<ProcessRunMutationResult | null> => {
    try {
        const response = await axios.post('/tasks/processes', { templateId });
        return response?.data || null;
    } catch (e) {
        return taskApiError(e);
    }
};

export const updateProcessRun = async (
    processId: number,
    data: ProcessRunSaveDataType,
): Promise<ProcessRunMutationResult | null> => {
    try {
        const response = await axios.patch(`/tasks/processes/${processId}`, data);
        return response?.data || null;
    } catch (e) {
        return taskApiError(e);
    }
};

export const saveProcessDiagramPositions = async (
    processId: number,
    data: ProcessDiagramPositionsSaveDataType,
): Promise<ProcessRunMutationResult | null> => {
    try {
        const response = await axios.patch(`/tasks/processes/${processId}/diagram-positions`, data);
        return response?.data || null;
    } catch (e) {
        return taskApiError(e);
    }
};

export const deleteProcessRun = async (processId: number): Promise<ProcessRunMutationResult | null> => {
    try {
        const response = await axios.delete(`/tasks/processes/${processId}`);
        return response?.data || null;
    } catch (e) {
        return taskApiError(e);
    }
};

export const launchProcessRun = async (processId: number): Promise<ProcessRunMutationResult | null> => {
    try {
        const response = await axios.post(`/tasks/processes/${processId}/launch`);
        return response?.data || null;
    } catch (e) {
        return taskApiError(e);
    }
};

export const deleteTaskTemplate = async (templateId: number): Promise<{ stat: boolean } | null> => {
    try {
        const response = await axios.delete(`/tasks/templates/${templateId}`);
        return response?.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};
